import React, { useState, useCallback, useRef } from 'react';
import type { GanttTask } from './ganttTimeUtils';
import { pmTheme } from './pmTheme';

interface GanttBarProps {
  task: GanttTask;
  x: number;
  width: number;
  y: number;
  barHeight: number;
  barColor: string;
  progressColor: string;
  barCornerRadius: number;
  isMilestone: boolean;
  isSummary: boolean;
  isDraggable: boolean;
  isResizable: boolean;
  onClick?: (task: GanttTask) => void;
  onDragStart?: (taskId: string, startX: number) => void;
  onDragMove?: (taskId: string, deltaX: number) => void;
  onDragEnd?: (taskId: string, deltaX: number) => void;
  onResizeEnd?: (taskId: string, deltaWidth: number) => void;
  showProgress: boolean;
  labelColumnWidth: number;
}

export const GanttBar: React.FC<GanttBarProps> = ({
  task,
  x,
  width,
  y,
  barHeight,
  barColor,
  progressColor,
  barCornerRadius,
  isMilestone,
  isSummary,
  isDraggable,
  isResizable,
  onClick,
  onDragStart,
  onDragMove,
  onDragEnd,
  onResizeEnd,
  showProgress,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartXRef = useRef(0);
  const dragModeRef = useRef<'move' | 'resize'>('move');

  const progress = Math.max(0, Math.min(100, task.progress ?? 0));
  const progressWidth = width * (progress / 100);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!isDraggable && !isResizable) return;
    e.preventDefault();
    e.stopPropagation();

    const svgEl = (e.target as SVGElement).closest('svg');
    if (!svgEl) return;

    const rect = svgEl.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    dragStartXRef.current = mouseX;

    // Check if near right edge for resize
    const barRightEdge = x + width;
    if (isResizable && Math.abs(mouseX - barRightEdge) < 8) {
      dragModeRef.current = 'resize';
      setIsResizing(true);
    } else if (isDraggable) {
      dragModeRef.current = 'move';
      setIsDragging(true);
      onDragStart?.(task.id, mouseX);
    }

    const handleMouseMove = (moveEvt: MouseEvent) => {
      const currentX = moveEvt.clientX - rect.left;
      const delta = currentX - dragStartXRef.current;
      if (dragModeRef.current === 'move') {
        onDragMove?.(task.id, delta);
      }
    };

    const handleMouseUp = (upEvt: MouseEvent) => {
      const currentX = upEvt.clientX - rect.left;
      const delta = currentX - dragStartXRef.current;
      if (dragModeRef.current === 'move') {
        onDragEnd?.(task.id, delta);
        setIsDragging(false);
      } else {
        onResizeEnd?.(task.id, delta);
        setIsResizing(false);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [isDraggable, isResizable, x, width, task.id, onDragStart, onDragMove, onDragEnd, onResizeEnd]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (isDragging || isResizing) return;
    e.stopPropagation();
    onClick?.(task);
  }, [isDragging, isResizing, onClick, task]);

  // Milestone: diamond marker
  if (isMilestone) {
    const size = barHeight * 0.5;
    const cx = x;
    const cy = y + barHeight / 2;
    const points = `${cx},${cy - size} ${cx + size},${cy} ${cx},${cy + size} ${cx - size},${cy}`;
    return (
      <g
        data-testid={`gantt-milestone-${task.id}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <polygon
          points={points}
          fill={task.color || barColor}
          stroke={isHovered ? pmTheme.text : 'none'}
          strokeWidth={isHovered ? 1.5 : 0}
        />
        <text
          x={cx + size + 4}
          y={cy + 4}
          fontSize={11}
          fill={pmTheme.text}
        >
          {task.name}
        </text>
        {/* Tooltip */}
        {isHovered && (
          <g>
            <rect
              x={cx + size + 2}
              y={cy - barHeight - 8}
              width={Math.max(task.name.length * 7 + 20, 120)}
              height={24}
              rx={4}
              fill={pmTheme.surface}
              opacity={0.9}
            />
            <text x={cx + size + 8} y={cy - barHeight + 7} fontSize={11} fill={pmTheme.text}>
              {task.name} — Milestone
            </text>
          </g>
        )}
      </g>
    );
  }

  // Summary bar (parent): thin bracket-style bar
  if (isSummary) {
    const bracketH = 6;
    return (
      <g
        data-testid={`gantt-summary-bar-${task.id}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleClick}
        style={{ cursor: 'pointer' }}
      >
        <rect
          x={x}
          y={y + barHeight / 2 - bracketH / 2}
          width={width}
          height={bracketH}
          fill={task.color || pmTheme.textMuted}
          rx={1}
        />
        {/* Left bracket */}
        <rect x={x} y={y + barHeight / 2 - bracketH} width={3} height={bracketH * 2} fill={task.color || pmTheme.textMuted} />
        {/* Right bracket */}
        <rect x={x + width - 3} y={y + barHeight / 2 - bracketH} width={3} height={bracketH * 2} fill={task.color || pmTheme.textMuted} />
        {/* Label */}
        <text x={x + 8} y={y + barHeight / 2 - bracketH - 2} fontSize={10} fill={pmTheme.text} fontWeight={600}>
          {task.name}
        </text>
        {showProgress && progress > 0 && (
          <text x={x + width + 6} y={y + barHeight / 2 + 4} fontSize={10} fill={pmTheme.textMuted}>
            {progress}%
          </text>
        )}
      </g>
    );
  }

  // Standard bar
  return (
    <g
      data-testid={`gantt-bar-${task.id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      style={{ cursor: isDraggable ? 'grab' : 'pointer' }}
    >
      {/* Background bar */}
      <rect
        x={x}
        y={y}
        width={width}
        height={barHeight}
        rx={barCornerRadius}
        ry={barCornerRadius}
        fill={task.color || barColor}
        stroke={isHovered ? pmTheme.text : 'transparent'}
        strokeWidth={isHovered ? 1.5 : 0}
        opacity={0.85}
      />

      {/* Progress fill */}
      {showProgress && progress > 0 && (
        <rect
          x={x}
          y={y}
          width={progressWidth}
          height={barHeight}
          rx={barCornerRadius}
          ry={barCornerRadius}
          fill={progressColor}
          opacity={0.9}
          data-testid={`gantt-bar-progress-${task.id}`}
        />
      )}

      {/* Bar label (inside bar if wide enough) */}
      {width > 60 && (
        <text
          x={x + 6}
          y={y + barHeight / 2 + 4}
          fontSize={11}
          fill={pmTheme.textInverse}
          style={{ pointerEvents: 'none' }}
        >
          {task.name.length > width / 7 ? task.name.substring(0, Math.floor(width / 7)) + '…' : task.name}
        </text>
      )}

      {/* Progress text */}
      {showProgress && progress > 0 && width > 80 && (
        <text
          x={x + width - 30}
          y={y + barHeight / 2 + 4}
          fontSize={10}
          fill={pmTheme.textInverse}
          opacity={0.8}
          style={{ pointerEvents: 'none' }}
        >
          {progress}%
        </text>
      )}

      {/* Drag handles */}
      {(isDraggable || isResizable) && isHovered && (
        <>
          {isDraggable && (
            <rect
              x={x}
              y={y}
              width={6}
              height={barHeight}
              rx={2}
              fill={pmTheme.text}
              opacity={0.3}
              style={{ cursor: 'grab' }}
            />
          )}
          {isResizable && (
            <rect
              x={x + width - 6}
              y={y}
              width={6}
              height={barHeight}
              rx={2}
              fill={pmTheme.text}
              opacity={0.3}
              style={{ cursor: 'ew-resize' }}
              data-testid={`gantt-bar-resize-${task.id}`}
            />
          )}
        </>
      )}

      {/* Tooltip on hover */}
      {isHovered && !isDragging && !isResizing && (
        <g>
          <rect
            x={x}
            y={y - 32}
            width={Math.max(200, task.name.length * 7 + 80)}
            height={26}
            rx={4}
            fill={pmTheme.surface}
            opacity={0.92}
          />
          <text x={x + 6} y={y - 15} fontSize={11} fill={pmTheme.text}>
            {task.name}
            {task.startDate ? ` | ${task.startDate.toLocaleDateString()}` : ''}
            {task.endDate ? ` – ${task.endDate.toLocaleDateString()}` : ''}
            {showProgress ? ` | ${progress}%` : ''}
          </text>
        </g>
      )}
    </g>
  );
};

GanttBar.displayName = 'GanttBar';
